import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useState, forwardRef } from 'react';
import { Input } from './ui/input';
import { getGoogleClientId, isGoogleAuthAvailable } from '../utils/googleAuth';

// Brand treatment for the form controls rendered inside the auth card:
// soft rounded inputs with a thin logo-gradient outline on focus, pill buttons
// and an ink primary action. Applied from the shell so every auth page matches.
const AUTH_CARD_CONTROLS = [
  '[&_input:not([type=checkbox]):not([type=radio])]:h-11',
  '[&_input:not([type=checkbox]):not([type=radio])]:rounded-xl',
  '[&_input:not([type=checkbox]):not([type=radio])]:bg-white',
  '[&_input:not([type=checkbox]):not([type=radio])]:border-[#DCD6C8]',
  '[&_input:not([type=checkbox]):not([type=radio]):focus]:border-transparent',
  '[&_input:not([type=checkbox]):not([type=radio]):focus]:[background:linear-gradient(#fff,#fff)_padding-box,linear-gradient(120deg,#EC008C,#0CBFF2)_border-box]',
  '[&_input:focus-visible]:ring-[3px]',
  '[&_input:focus-visible]:ring-pink/10',
  '[&_button]:rounded-full',
  '[&_[role=tablist]]:rounded-full',
  '[&_[role=tablist]]:p-1',
  '[&_[role=tablist]]:h-11',
  '[&_[role=tab]]:h-9',
  '[&_button[type=submit]]:h-12',
  '[&_button[type=submit]]:bg-ink',
  '[&_button[type=submit]]:text-paper',
  '[&_button[type=submit]]:font-bold',
  '[&_button[type=submit]:hover]:bg-ink/90',
].join(' ');

/** Shared shell for the sign-in and sign-up pages. */
export const AuthLayout = ({ title, subtitle, children, footer }) => (
  <div className="relative min-h-screen bg-paper flex flex-col items-center justify-center px-4 sm:px-6 py-12 overflow-hidden">
    {/* Soft brand glows behind the card */}
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="glow-pink absolute -top-32 -left-32 h-[28rem] w-[28rem] sm:left-[8%] sm:top-[4%] opacity-70" />
      <div className="glow-blue absolute -bottom-40 -right-32 h-[30rem] w-[30rem] sm:right-[6%] sm:bottom-[2%] opacity-70" />
    </div>

    <motion.main
      id="main-content"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative w-full max-w-md"
    >
      <div className="text-center mb-8">
        <Link
          to="/"
          className="inline-block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          aria-label="Azamein home"
        >
          <img src="/azamein-logo.png" alt="Azamein" width="254" height="72" className="h-11 w-auto mx-auto" />
        </Link>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-ink mt-7">{title}</h1>
        {subtitle && <p className="text-[15px] leading-relaxed text-[#4A4640] mt-2">{subtitle}</p>}
      </div>

      <div
        className={`gradient-border gb-glass backdrop-blur-sm rounded-[28px] p-6 sm:p-9 shadow-[0_30px_60px_-36px_rgba(26,26,26,0.35)] ${AUTH_CARD_CONTROLS}`}
      >
        {children}
      </div>

      {footer && <div className="mt-6 text-center text-sm text-[#4A4640]">{footer}</div>}
    </motion.main>
  </div>
);

/** Inline error banner for forms. */
export const FormError = ({ message, id }) =>
  message ? (
    <div
      id={id}
      role="alert"
      className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
    >
      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  ) : null;

/** Password input with a show/hide toggle. */
export const PasswordInput = forwardRef(({ className = '', ...props }, ref) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input ref={ref} type={visible ? 'text' : 'password'} className={`pr-10 ${className}`} {...props} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-0 top-0 h-full px-3 text-ink/50 hover:text-ink focus-visible:outline-none focus-visible:text-ink"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});
PasswordInput.displayName = 'PasswordInput';

/** Start the Google OAuth redirect flow. Returns false if not configured. */
export const startGoogleSignIn = () => {
  if (!isGoogleAuthAvailable()) return false;
  const clientId = getGoogleClientId();
  const redirectUri = `${window.location.origin}/auth/google`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    access_type: 'offline',
    prompt: 'consent',
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  return true;
};

export const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

export const OrDivider = ({ label = 'or' }) => (
  <div className="relative my-6" role="separator">
    <div className="absolute inset-0 flex items-center">
      <span className="w-full border-t border-[#E6E1D6]" />
    </div>
    <div className="relative flex justify-center text-xs uppercase">
      <span className="bg-white px-3 tracking-[0.14em] font-semibold text-[#9C978C] rounded-full">{label}</span>
    </div>
  </div>
);
